import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  DialerCreateRequest,
  DialerDetail,
  DialerGroupPatchRequest,
  DialerGroupSearchRow,
  DialerRow,
  DialerUpdateRequest,
  DialerUsageRequest,
  DialerUsageRow,
  DialerwiseUsageRequest,
  DialerwiseUsageRow,
  SetDialerSuccessRequest,
} from './types';

export const dialerKeys = {
  all: ['dialers'] as const,
  list: ['dialers', 'list'] as const,
  detail: (dialerId: string | number) => ['dialers', 'detail', dialerId] as const,
  groups: (dialerId: string | number) => ['dialers', 'groups', dialerId] as const,
};

/** GET /api/obd/dialers/list */
export function useDialers(options?: { refetchIntervalMs?: number }) {
  return useQuery({
    queryKey: dialerKeys.list,
    queryFn: () => api.get<DialerRow[]>('/api/obd/dialers/list'),
    staleTime: 30_000,
    refetchInterval: options?.refetchIntervalMs ?? false,
    select: (data) => ensureArray<DialerRow>(data),
  });
}

/** GET /api/obd/dialer/detail/{dialerId} */
export function useDialerDetail(dialerId: string | number | null) {
  return useQuery({
    queryKey: dialerKeys.detail(dialerId ?? 'none'),
    queryFn: () => api.get<DialerDetail>(`/api/obd/dialer/detail/${dialerId}`),
    enabled: Boolean(dialerId),
    staleTime: 30_000,
  });
}

function useInvalidateDialers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: dialerKeys.all });
}

export function useCreateDialer() {
  const invalidate = useInvalidateDialers();
  return useMutation({
    mutationFn: (req: DialerCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/dialer', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateDialer() {
  const invalidate = useInvalidateDialers();
  return useMutation({
    mutationFn: (req: DialerUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/dialer/update', req),
    onSuccess: () => invalidate(),
  });
}

/**
 * Inline group attach/detach used by the Update page. The legacy endpoint
 * mutates server-side immediately on each Add or Remove click.
 */
export function usePatchDialerGroup() {
  const invalidate = useInvalidateDialers();
  return useMutation({
    mutationFn: (req: DialerGroupPatchRequest) =>
      api.post<{ message?: string }>('/api/obd/dialers/update', req),
    onSuccess: () => invalidate(),
  });
}

/** GET /api/obd/dialer/group/{dialerId} — groups attached to a dialer. */
export function useDialerGroups(dialerId: string | number | null) {
  return useQuery({
    queryKey: dialerKeys.groups(dialerId ?? 'none'),
    queryFn: () =>
      api.get<DialerGroupSearchRow[] | DialerGroupSearchRow>(
        `/api/obd/dialer/group/${dialerId}`,
      ),
    enabled: Boolean(dialerId),
    staleTime: 30_000,
    select: (data) =>
      Array.isArray(data) ? ensureArray<DialerGroupSearchRow>(data) : [data],
  });
}

/** POST /api/obd/dialer/usage with { startDate, endDate }. */
export function useDialerUsage() {
  return useMutation({
    mutationFn: (req: DialerUsageRequest) =>
      api
        .post<DialerUsageRow[]>('/api/obd/dialer/usage', req)
        .then((res) => ensureArray<DialerUsageRow>(res)),
  });
}

/* ============================================================
 *  Special-dialer + cross-dialer rollup endpoints
 * ============================================================ */

/** GET /api/obd/dialers/special — list of dialers flagged "special". */
export function useSpecialDialers() {
  return useQuery({
    queryKey: ['dialers', 'special'] as const,
    queryFn: () => api.get<DialerRow[]>('/api/obd/dialers/special'),
    staleTime: 30_000,
    select: (data) => ensureArray<DialerRow>(data),
  });
}

/**
 * GET /api/obd/dialers/getsuccess/:dialerId/:campaignId — current success
 * percentage pinned for this (dialer, campaign) pair. Response is a bare
 * number (0–100). Useful to prefill the percentage input.
 */
export function useDialerSuccessRate(
  dialerId: number | string | null,
  campaignId: number | string | null,
) {
  return useQuery({
    queryKey: ['dialers', 'success', dialerId, campaignId] as const,
    queryFn: () =>
      api.get<number>(`/api/obd/dialers/getsuccess/${dialerId}/${campaignId}`),
    enabled: Boolean(dialerId) && Boolean(campaignId),
    staleTime: 30_000,
  });
}

/** POST /api/obd/dialers/setsuccess — pin a campaign to a special dialer. */
export function useSetDialerSuccessRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: SetDialerSuccessRequest) =>
      api.post<{ message?: string }>('/api/obd/dialers/setsuccess', req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dialers'] });
      qc.invalidateQueries({ queryKey: ['campaigns', 'allocation'] });
    },
  });
}

/**
 * POST /api/obd/dialerwise/totalUsage — cross-dialer call duration rollup.
 * Used by the SADMIN dashboard widget; this hook is a mutation because the
 * page submits a date range on-demand rather than refetching on every render.
 */
export function useDialerwiseUsage() {
  return useMutation({
    mutationFn: (req: DialerwiseUsageRequest) =>
      api
        .post<DialerwiseUsageRow[]>('/api/obd/dialerwise/totalUsage', req)
        .then((res) => ensureArray<DialerwiseUsageRow>(res)),
  });
}
